import React from 'react';
import { Loader2 } from 'lucide-react';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: React.ReactNode;
  fullWidth?: boolean;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    `bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-500 text-white
     hover:shadow-glow-primary hover:scale-[1.02]
     active:scale-[0.98] active:shadow-md
     focus:ring-primary-500/30`,
  secondary:
    `bg-gradient-to-r from-secondary-500 to-secondary-600 text-white
     hover:shadow-glow-primary hover:scale-[1.02]
     active:scale-[0.98]
     focus:ring-secondary-500/30`,
  ghost:
    `bg-transparent text-neutral-700 dark:text-neutral-300
     hover:bg-neutral-100/80 dark:hover:bg-white/10 hover:scale-[1.02]
     active:bg-neutral-200 dark:active:bg-white/15 active:scale-[0.98]
     focus:ring-neutral-500/20`,
  danger:
    `bg-gradient-to-r from-error-500 to-rose-600 text-white
     hover:shadow-glow-rose hover:scale-[1.02]
     active:scale-[0.98]
     focus:ring-error-500/30`,
  outline:
    `bg-white/60 dark:bg-[#1e1e2e]/60 backdrop-blur-sm text-neutral-700 dark:text-neutral-300 border border-neutral-200/80 dark:border-indigo-500/20
     hover:bg-white dark:hover:bg-[#1e1e2e] hover:border-primary-200 dark:hover:border-indigo-500/40 hover:text-primary-600 dark:hover:text-primary-400 hover:scale-[1.02]
     active:scale-[0.98]
     focus:ring-primary-500/20`,
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm gap-1.5 rounded-lg',
  md: 'px-4 py-2.5 text-sm gap-2 rounded-xl',
  lg: 'px-6 py-3 text-base gap-2.5 rounded-xl',
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  fullWidth = false,
  className = '',
  disabled,
  children,
  ...props
}) => {
  return (
    <button
      className={`
        inline-flex items-center justify-center font-semibold
        transition-all duration-200 ease-out
        focus:outline-none focus:ring-2 focus:ring-offset-2
        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
        ${variantStyles[variant]}
        ${sizeStyles[size]}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `.trim()}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <div className="relative w-5 h-5">
          <div className="absolute inset-0 rounded-full border-2 border-white/30" />
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : icon ? (
        <span className="shrink-0">{icon}</span>
      ) : null}
      {children}
    </button>
  );
};
